import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from './index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../../components/ToastMsg";
import NoResultsFound from "../../../../components/NoResultsFound";
import TableSkeleton from "../../../../components/Skeleton";
import Dropdown from "../../../../components/Dropdown/Dropdown";
import { useNavigate, useParams } from "react-router-dom";
import { addNewCourse, getCourseById } from "../../../../redux/features/courses/coursesService";
import { getAllCourseCategories } from "../../../../redux/features/courseCategories/courseCategoriesService";
import { getAllStaffMembers } from "../../../../redux/features/staff/staffService";
import GridLoader from "react-spinners/GridLoader";

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const IMGRATIO = 9 / 16;

const CourseDetails = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const fileInputRef = useRef(null);
    const mentorDropdownRef = useRef(null);
    const courseCategoryDropdownRef = useRef(null);

    const { isCourseLoading, course } = useSelector((state) => state.course.course);
    const { isCourseCreating } = useSelector((state) => state.course.newCourse);

    const { courseCategories } = useSelector((state) => state.courseCategory.allCourseCategories);
    const { staffMembers } = useSelector((state) => state.staff.allStaffMembers);

    const [formData, setFormData] = useState({
        title: "",
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setDisabledBtn] = useState(false);
    const [thumbnail, setThumbnail] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [initialCourse, setInitialCourse] = useState(null);
    const [selectedMentor, setSelectedMentor] = useState(null);
    const [selectedCourseCategory, setSelectedCourseCategory] = useState(null);

    const fetchCourseCategories = useCallback(
        (filters = {}) => {
            const params = {
                status: true,
                ...filters,
            };
            dispatch(getAllCourseCategories(params));
        },
        [dispatch]
    );

    const fetchStaffMemebrs = useCallback(
        (filters = {}) => {
            const params = {
                status: true,
                role_name: 'mentor',
                ...filters,
            };
            dispatch(getAllStaffMembers(params));
        },
        [dispatch]
    );

    useEffect(() => {
        fetchCourseCategories();
        fetchStaffMemebrs();
    }, [fetchCourseCategories, fetchStaffMemebrs]);

    const getCourseDetails = useCallback(async () => {
        try {
            const response = await dispatch(getCourseById({ id })).unwrap();

            setInitialCourse(response);
            setSelectedCourse(response);

            if (response.user) {
                setSelectedMentor({
                    id: response.user.id,
                    name: response.user.full_name,
                });
            }

            if (response.course_category) {
                setSelectedCourseCategory({
                    id: response.course_category.id,
                    name: response.course_category.name,
                });
            }

        } catch (error) {
            console.error("Failed to fetch course details", error);
        }
    }, [dispatch, id]);

    useEffect(() => {
        getCourseDetails();
    }, [getCourseDetails]);

    const handleSelect = (option) => {
        setFormData({ ...formData, courseCategoryId: option.id });
        setErrorMessages({ ...errorMessages, category: "" });
        setSelectedCourseCategory({
            id: option.id,
            name: option.name
        });
    };

    const handleUserSelect = (option) => {
        setFormData({ ...formData, userId: option.id });
        setErrorMessages({ ...errorMessages, user: "" });
        setSelectedMentor({
            id: option.id,
            name: option.name
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedCourse((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const validateFile = (file) => {
        if (file) {
            if (!file.type.startsWith("image/")) {
                setErrorMessages({ ...errorMessages, file: "Only image files are allowed." });
                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                setErrorMessages({ ...errorMessages, file: "File size exceeds 2MB limit." });
                return;
            }

            setErrorMessages({ ...errorMessages, file: "" });
            setFormData({ ...formData, file });
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedFile = event.dataTransfer.files[0];

        if (selectedFile) {
            validateFile(selectedFile);
        }
    };

    const handleFileChange = (event) => {
        if (event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            validateFile(selectedFile);
        }
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    // Validate input
    const isValidStaffMember = () => {
        const errors = {};
        const trimmedName = formData.title.trim();
        if (!trimmedName) {
            errors.title = "Name is required.";
        }
        if (!formData.courseCategoryId) {
            errors.category = "Course Category is required.";
        }
        if (!formData.userId) {
            errors.user = "Mentor is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewStaffMember = async () => {
        if (isValidStaffMember()) {
            setDisabledBtn(true);
            try {
                const response = await dispatch(addNewCourse(formData)).unwrap();
                setDisabledBtn(false);
                setFormData({
                    full_name: '',
                    email: '',
                    phone_number: '',
                });
                if (roleDropdownRef.current) {
                    roleDropdownRef.current.clearSelection();
                }
                showToast('success', response.message);
            }
            catch (error) {
                setDisabledBtn(false);
                showToast('error', error.message);
            };
        }
    };

    return (
        <div className={`card-section`}>
            {selectedCourseCategory === null ?
                <div className={`loader-style`}>
                    <GridLoader color="#E11F39" />
                </div>
                :
                <>
                    <div className={`main-card`}>
                        <h1 className={`form-title`}>Course Details</h1>
                        <div className={styles.formContainer}>
                            <form className={styles.coordinatorForm} noValidate>
                                <div className="form-input-row">
                                    <div className="form-input-col md-col">
                                        <div className="form-input-group">
                                            <label className="form-label">
                                                Title<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="title"
                                                value={selectedCourse?.title || ''}
                                                onChange={handleInputChange}
                                                placeholder="Enter Title"
                                                className="form-input-default"
                                            />
                                            {errorMessages.title && (
                                                <p className="text-danger">{errorMessages.title}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="form-input-row">
                                    <div className="form-input-col md-col">
                                        <div className="form-input-group">
                                            <label className="form-label">
                                                Slug<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="slug"
                                                value={selectedCourse?.slug || ''}
                                                onChange={handleInputChange}
                                                placeholder="Enter Slug"
                                                className="form-input-default"
                                            />
                                            {errorMessages.slug && (
                                                <p className="text-danger">{errorMessages.slug}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="form-input-row">
                                    <div className="form-input-col md-col">
                                        <div className="form-input-group">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                value={selectedCourse?.description || ''}
                                                name="description"
                                                onChange={handleInputChange}
                                                className="form-input-default"
                                                rows={3}
                                                placeholder="Describe the course"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-input-row">
                                    <div className="form-input-col md-col">
                                        <div className="form-input-group">
                                            <label className="form-label">Read More</label>
                                            <textarea
                                                value={selectedCourse?.read_more || ''}
                                                onChange={handleInputChange}
                                                className="form-input-default"
                                                rows={3}
                                                placeholder="Read More"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-input-row">
                                    <div className="form-input-col md-col">
                                        <div className="form-input-group">
                                            <label className="form-label">
                                                Price<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="price"
                                                value={selectedCourse?.price || ''}
                                                onChange={handleInputChange}
                                                placeholder="Enter Price"
                                                className="form-input-default"
                                            />
                                            {errorMessages.price && (
                                                <p className="text-danger">{errorMessages.price}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="form-input-row">
                                    <div className="form-input-col md-col">
                                        <div className="form-input-group">
                                            <label className="form-label">Course Category</label>
                                            <Dropdown
                                                ref={courseCategoryDropdownRef}
                                                options={courseCategories}
                                                placeholder="Choose an Option"
                                                onSelect={handleSelect}
                                                value={selectedCourseCategory?.name}
                                            />
                                            {errorMessages.category && (
                                                <p className={`text-danger`}>{errorMessages.category}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="form-input-row">
                                    <div className="form-input-col md-col">
                                        <div className="form-input-group">
                                            <label className="form-label">Mentor</label>
                                            <Dropdown
                                                ref={mentorDropdownRef}
                                                options={staffMembers.map(member => ({
                                                    ...member,
                                                    name: member.full_name
                                                }))}
                                                placeholder="Choose an Option"
                                                value={selectedMentor?.name}
                                                onSelect={handleUserSelect}
                                            />
                                            {errorMessages.user && (
                                                <p className={`text-danger`}>{errorMessages.user}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={`form-input-row`}>
                                    <div className={`form-input-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Image<span className={`required-star`}>*</span>
                                            </label>
                                            <div
                                                onDrop={handleDrop}
                                                onDragOver={(event) => event.preventDefault()}
                                                onClick={handleClick}
                                                className={`drop-input`}
                                            >
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                    style={{ display: "none" }}
                                                />
                                                {((!thumbnail) && (!selectedCourse?.image_url)) ? <p>Drag & Drop a file here or click to select</p>
                                                    :
                                                    <div className={`inserted-file`}>
                                                        <div className={`file-card`}>
                                                            {!thumbnail ?
                                                                <img src={selectedCourse?.image_url} alt='File Type' className={`${styles.thumbnailImg} left-icon`} />
                                                                :
                                                                <img src={URL.createObjectURL(thumbnail)} alt='File Type' className={`${styles.thumbnailImg} left-icon`} />
                                                            }
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                            {errorMessages.file && (
                                                <p className={`text-danger`}>{errorMessages.file}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    className="btn-primary"
                                    type="button"
                                    disabled={disabledBtn}
                                    onClick={insertNewStaffMember}
                                >
                                    {isCourseCreating ? <span>Loading...</span> : `Create`}
                                </button>
                            </form>
                        </div>
                    </div>
                    <div className={`search-card`}>
                        <div className={`search-card-header`}>
                            <h1 className={`form-title`}>Videos</h1>
                        </div>
                        <div className={`search-card-body`}>
                            {isCourseLoading ?
                                <TableSkeleton columns={["Title", "Description", "Slug", "Order", "Actions"]} rowCount={10} height={20} />
                                : course?.videos.length > 0 ?
                                    <div className={`table-container`}>
                                        <table className={`table`}>
                                            <thead className={`thead`}>
                                                <tr>
                                                    <th className={`theadColumnOne`}>Title</th>
                                                    <th>Description</th>
                                                    <th>Slug</th>
                                                    <th>Order</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className={`tbody`}>
                                                {course?.videos?.map((video) => (
                                                    <tr key={video.id}>
                                                        <td className={`tdataColumnOne`}>
                                                            {video.title}
                                                        </td>
                                                        <td>{video.description}</td>
                                                        <td>{video.slug}</td>
                                                        <td>{video.order}</td>
                                                        <td className={`actionsBtn`}>
                                                            <button
                                                                className={course.status === 'Active' ? `btn-inactive` : `btn-active`}
                                                                onClick={() => {
                                                                    setSelectedStaffMemberId(course.id);
                                                                    course.status === 'Active' ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                                }
                                                                }>{course.status === 'Active' ? 'Deactivate' : 'Acitvate'}</button>
                                                            {/* <button className={`view-btn`} onClick={() => openMentor(course.id)}>View</button> */}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    :
                                    <NoResultsFound />
                            }
                        </div>
                    </div>
                </>
            }
        </div>
    );
};

export default CourseDetails;